import CustomFontsProvider from 'client/context/CustomFontsContext'
import IconsProvider from 'client/context/IconsContext'
import {
  useSidebarVisibilityActionsContext,
  useSidebarVisibilityContext,
} from 'client/context/SidebarVisibilityContext'
import useManagement from 'client/hooks/useManagement'
import { selectors } from 'client/reducers/managementReducer'
import { ToggleSidebarIcon } from 'common/icons/toggle-sidebar-icon'
import React, { useEffect, useRef, memo, useLayoutEffect } from 'react'
import { twMerge } from 'tailwind-merge'
import {
  SIDEBAR_ROOT_ID,
  SIDEBAR_BOTTOM_CONTENT_ID,
  SIDEBAR_CONTENT_ID,
} from 'client/constants/editorIds'
import { SidebarDevTools } from './sidebar-dev-tools'
import { SidebarContent } from './sidebar-content'

export const Sidebar = memo(() => {
  const sidebarRootRef = useRef<HTMLDivElement>(null)
  const sidebarContentRef = useRef<HTMLDivElement>(null)
  const sidebarContentInnerRef = useRef<HTMLDivElement>(null)
  const sidebarBottomContentRef = useRef<HTMLDivElement>(null)

  const editingId = useManagement(selectors.getEditingId)

  // biome-ignore lint/correctness/useExhaustiveDependencies: scroll reset on entity switch
  useEffect(() => {
    if (sidebarContentInnerRef.current) {
      sidebarContentInnerRef.current.scrollTop = 0
    }
  }, [editingId])

  const updateSidebarBottomContentHeight = () => {
    if (sidebarContentRef.current) {
      const sidebarRootHeight =
        sidebarRootRef.current?.getBoundingClientRect().height ?? 0
      const sidebarBottomContentHeight =
        sidebarBottomContentRef.current?.getBoundingClientRect().height ?? 0
      sidebarContentRef.current.style.height = `${sidebarRootHeight - sidebarBottomContentHeight}px`
    }
  }

  useLayoutEffect(() => {
    if (sidebarRootRef.current) {
      updateSidebarBottomContentHeight()

      const observer = new ResizeObserver(() => {
        updateSidebarBottomContentHeight()
      })
      observer.observe(sidebarRootRef.current)

      return () => observer.disconnect()
    }
  })

  useLayoutEffect(() => {
    if (sidebarBottomContentRef.current) {
      updateSidebarBottomContentHeight()

      const observer = new ResizeObserver(() => {
        updateSidebarBottomContentHeight()
      })
      observer.observe(sidebarBottomContentRef.current)
      return () => observer.disconnect()
    }
  })

  const isVisible = useSidebarVisibilityContext()
  return (
    <aside
      ref={sidebarRootRef}
      id={SIDEBAR_ROOT_ID}
      className="relative h-[calc(100vh-var(--layout-header-height))] border-border200 border-r bg-bgPrimaryBase"
    >
      <div
        ref={sidebarContentRef}
        id={SIDEBAR_CONTENT_ID}
        className={twMerge('relative h-full')}
      >
        <div
          ref={sidebarContentInnerRef}
          className={twMerge(
            'h-full overflow-y-auto p-[var(--sidebar-padding)]',
            !isVisible && 'hidden',
          )}
        >
          <IconsProvider>
            <CustomFontsProvider>
              <SidebarContent />
            </CustomFontsProvider>
          </IconsProvider>
        </div>
      </div>
      <div
        ref={sidebarBottomContentRef}
        id={SIDEBAR_BOTTOM_CONTENT_ID}
        className={twMerge(
          'flex w-full flex-col gap-1.5 px-[var(--sidebar-padding)]',
          '[&>*:first-of-type]:pt-[var(--sidebar-padding)] [&>*:last-of-type]:pb-[var(--sidebar-padding)]',
          !isVisible && 'hidden',
        )}
      >
        <SidebarDevTools />
      </div>
      <ToggleSidebarButton />
    </aside>
  )
})

const ToggleSidebarButton = () => {
  const isVisible = useSidebarVisibilityContext()
  const { toggleSidebar } = useSidebarVisibilityActionsContext()

  return (
    <button
      onClick={toggleSidebar}
      type="button"
      className={twMerge(
        'absolute top-[calc(50%-var(--layout-header-height))] left-full z-50 h-14 w-[18px]',
        'flex cursor-pointer items-center justify-center text-bgTertiary hover:text-gray-150',
      )}
    >
      <span className="-z-10 absolute top-0 left-0 transition-colors">
        <ToggleSidebarIcon />
      </span>
      {isVisible ? (
        <i className="fa-regular fa-chevron-left text-iconSecondary" />
      ) : (
        <i className="fa-regular fa-chevron-right text-iconSecondary" />
      )}
    </button>
  )
}
